/**
 * Amount Input Component
 * 
 * Reusable amount and currency input with optional quick-tap amounts.
 * Memoized to prevent re-renders when other form fields change.
 */

import { AlertCircle } from 'lucide-react';
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from '@/lib/utils/currency';
import { memo } from 'react';

interface AmountInputProps {
    amount: string;
    currency: string;
    onAmountChange: (amount: string) => void;
    onCurrencyChange: (currency: string) => void;
    error?: string;
    quickAmounts?: number[];
    onQuickAmountSelect?: (amount: number) => void;
}

function AmountInputComponent({
    amount,
    currency,
    onAmountChange,
    onCurrencyChange,
    error,
    quickAmounts,
    onQuickAmountSelect
}: AmountInputProps) {
    return (
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount *
            </label>
            <div className="flex gap-2">
                <div className="flex-1 flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500">
                    <span className="text-gray-600 mr-2">{getCurrencySymbol(currency)}</span>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => onAmountChange(e.target.value)}
                        className="w-full outline-none text-lg font-mono"
                        required
                    />
                </div>
                <select
                    value={currency}
                    onChange={(e) => onCurrencyChange(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                >
                    {SUPPORTED_CURRENCIES.map((curr) => (
                        <option key={curr} value={curr}>
                            {curr}
                        </option>
                    ))}
                </select>
            </div>

            {/* Quick-tap amounts */}
            {quickAmounts && quickAmounts.length > 0 && onQuickAmountSelect && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {quickAmounts.map((amountValue, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => onQuickAmountSelect(amountValue)}
                            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-teal-100 hover:text-teal-700 rounded-lg transition-colors font-medium"
                        >
                            {getCurrencySymbol(currency)}{amountValue}
                        </button>
                    ))}
                </div>
            )}

            {error && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </p>
            )}
        </div>
    );
}

/**
 * Memoized version prevents re-renders when other form fields change.
 * Only re-renders when amount, currency, or error props change.
 */
export const AmountInput = memo(AmountInputComponent);
